/**
 * Customer queries and mutations.
 *
 * Each mutation seeds the detail cache from its own response and invalidates
 * the lists, the same rule the pilot queries follow: the response carries the
 * record's new version, and a refetch racing it could hand a form a version
 * that is already stale. Lists are invalidated because a saved change can move
 * a customer in or out of the `overdue` filter.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.3
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  CreateCustomerRequest,
  CustomerDetail,
  ListCustomersQuery,
  UpdateCustomerRequest,
} from '@nexus/contracts';

import { queryKeys } from '../../../lib/query-keys';

import * as customersApi from './customers.api';
import type { CustomerPage } from './customers.api';

export function useCustomers(query: ListCustomersQuery): UseQueryResult<CustomerPage> {
  return useQuery({
    queryKey: queryKeys.customers.list(query),
    queryFn: () => customersApi.listCustomers(query),
  });
}

export function useCustomer(id: string): UseQueryResult<CustomerDetail> {
  return useQuery({
    queryKey: queryKeys.customers.detail(id),
    queryFn: () => customersApi.getCustomer(id),
  });
}

/** Seeds the detail cache and invalidates the lists. */
function useApplyCustomer(): (customer: CustomerDetail) => void {
  const queryClient = useQueryClient();

  return (customer: CustomerDetail) => {
    queryClient.setQueryData(queryKeys.customers.detail(customer.id), customer);
    void queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
  };
}

export function useCreateCustomer(): UseMutationResult<
  CustomerDetail,
  Error,
  CreateCustomerRequest
> {
  const apply = useApplyCustomer();

  return useMutation({
    mutationFn: (body: CreateCustomerRequest) => customersApi.createCustomer(body),
    onSuccess: apply,
  });
}

export function useUpdateCustomer(
  id: string,
): UseMutationResult<CustomerDetail, Error, UpdateCustomerRequest> {
  const apply = useApplyCustomer();

  return useMutation({
    mutationFn: (body: UpdateCustomerRequest) => customersApi.updateCustomer(id, body),
    onSuccess: apply,
  });
}
